<template>
  <div class="bg-white shadow-sm border border-slate-200 overflow-hidden">
    <div class="px-6 pt-2 pb-1">
      <div class="flex items-baseline gap-2 flex-wrap">
        <h2 class="text-base font-bold text-[#0d4169] text-left">
          {{ resolvedCompanyName }}
        </h2>
        <span class="text-sm font-bold text-slate-600">Purchase Declaration</span>
      </div>
    </div>

    <FinanceToast :show="toast.show" :variant="toast.variant">{{ toast.message }}</FinanceToast>

    <form class="px-6 pb-5 pt-2" @submit.prevent="generateReport">
      <div class="flex flex-col md:flex-row md:items-end md:flex-wrap gap-x-4 gap-y-2">
        <div class="flex flex-col gap-1 w-full md:w-auto md:min-w-[180px]">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">From</label>
          <input
            v-model="form.fromDate"
            type="date"
            required
            class="px-2.5 py-1 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
          />
        </div>

        <div class="flex flex-col gap-1 w-full md:w-auto md:min-w-[180px]">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Upto</label>
          <input
            v-model="form.uptoDate"
            type="date"
            class="px-2.5 py-1 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
          />
        </div>

        <div class="flex flex-col gap-1 w-full md:w-auto">
          <button
            type="submit"
            :disabled="isLoadingReport"
            class="px-5 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <span v-if="isLoadingReport" class="inline-flex items-center gap-2">
              <span class="h-4 w-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin"></span>
              Generating...
            </span>
            <span v-else>Generate</span>
          </button>
        </div>
      </div>
    </form>

    <div class="px-2 pb-6 pt-2">
      <template v-if="showReportTable">
        <div v-if="isLoadingReport" class="py-12 flex justify-center items-center">
          <div class="h-8 w-8 border-2 border-[#0d4169]/30 border-t-transparent rounded-full animate-spin"></div>
        </div>

        <div v-else class="w-full">
          <div class="flex items-center justify-between gap-3 mb-3 px-4 flex-wrap">
            <p class="text-[0.78rem] font-semibold text-[#1e293b] m-0">
              {{ reportPeriodTitle }}
            </p>
            <button
              type="button"
              @click="downloadPurchaseDeclarationPdf"
              :disabled="isDownloadingPdf || reportRows.length === 0"
              class="inline-flex items-center gap-2 px-4 py-1.5 bg-gray-300 text-gray-700 text-xs font-bold rounded-md shadow-md hover:bg-gray-400 transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span
                v-if="isDownloadingPdf"
                class="h-3.5 w-3.5 border-2 border-white/70 border-t-transparent rounded-full animate-spin"
              ></span>
              <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                class="w-3.5 h-3.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              <span>{{ isDownloadingPdf ? 'Downloading...' : 'Download PDF' }}</span>
            </button>
          </div>

          <div class="purchase-table-scroll relative overflow-auto min-h-[220px] max-h-[calc(100vh-260px)] px-2">
            <table class="purchase-table w-full min-w-[1280px] border-collapse">
              <thead>
                <tr>
                  <th class="purchase-col-no">No.</th>
                  <th>Seller TIN</th>
                  <th>Seller Name</th>
                  <th>Receipt Number</th>
                  <th>Receipt Date</th>
                  <th>MRC Number</th>
                  <th>Purchase Category</th>
                  <th>Purchase Type</th>
                  <th>Item Name</th>
                  <th class="num">Qty</th>
                  <th class="num">Unit Price</th>
                  <th class="num">Total Price</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in reportRows"
                  :key="row.lineId || row.no"
                  class="hover:bg-slate-50 transition-colors border-b"
                >
                  <td class="purchase-col-no">{{ row.no }}</td>
                  <td>{{ row.sellerTin || '—' }}</td>
                  <td>{{ row.sellerName || '—' }}</td>
                  <td>{{ row.receiptNumber || '—' }}</td>
                  <td>{{ formatDisplayDate(row.receiptDate) }}</td>
                  <td>{{ row.mrcNumber || '—' }}</td>
                  <td>{{ row.purchaseCategory || '—' }}</td>
                  <td>{{ row.purchaseType || '—' }}</td>
                  <td>{{ row.itemName || '—' }}</td>
                  <td class="num">{{ fmtQty(row.qty) }}</td>
                  <td class="num">{{ fmt(row.unitPrice) }}</td>
                  <td class="num">{{ fmt(row.totalPrice) }}</td>
                </tr>
                <tr v-if="reportRows.length === 0">
                  <td colspan="12" class="text-center text-slate-400 italic py-5">
                    No purchase lines found for the selected date range.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>

      <p
        v-else
        class="text-sm text-slate-500 text-center py-10 m-0 italic"
      >
        Select a from date and click Generate to view the purchase report.
      </p>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import { useStore } from 'vuex'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import FinanceToast from '../FinanceToast.vue'
import { apiFetch } from '../../../utils/api'

const store = useStore()

const currentUser = computed(() => store.getters.currentUser || store.state.user || {})

const resolvedCompanyName = computed(() => {
  const user = currentUser.value || {}
  return (
    user?.company?.name ||
    user?.companyName ||
    user?.company_name ||
    user?.company?.company_name ||
    'Company Name'
  )
})

const printCompanyName = computed(() => resolvedCompanyName.value || 'Company')

const form = reactive({
  fromDate: '',
  uptoDate: ''
})

const toast = reactive({
  show: false,
  message: '',
  variant: 'success'
})
let toastTimer = null

function showToast(message, variant = 'success') {
  if (toastTimer) {
    window.clearTimeout(toastTimer)
    toastTimer = null
  }
  toast.message = message
  toast.variant = variant
  toast.show = true
  const duration = variant === 'error' ? 5000 : 3000
  toastTimer = window.setTimeout(() => {
    toast.show = false
    toast.variant = 'success'
    toastTimer = null
  }, duration)
}

const isLoadingReport = ref(false)
const isDownloadingPdf = ref(false)
const reportRows = ref([])
const hasGenerated = ref(false)
let reportRequestId = 0

const showReportTable = computed(() => hasGenerated.value)

function formatDisplayDate(isoDate) {
  if (!isoDate) return '—'
  const d = new Date(`${isoDate}T00:00:00`)
  if (Number.isNaN(d.getTime())) return isoDate
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}

function formatPdfDate(isoDate) {
  if (!isoDate) return '______________________'
  return formatDisplayDate(isoDate)
}

const reportPeriodTitle = computed(() => {
  if (form.fromDate && form.uptoDate) {
    return `Purchase Declaration for ${formatDisplayDate(form.fromDate)} - ${formatDisplayDate(form.uptoDate)}`
  }
  if (form.fromDate) {
    return `Purchase Declaration from ${formatDisplayDate(form.fromDate)}`
  }
  return 'Purchase Declaration'
})

function fmt(value) {
  if (value === null || value === undefined || value === '') return ''
  const num = Number(value)
  if (Number.isNaN(num)) return ''
  return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function fmtQty(value) {
  if (value === null || value === undefined || value === '') return ''
  const num = Number(value)
  if (Number.isNaN(num)) return ''
  if (Number.isInteger(num)) return String(num)
  return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })
}

function lineAmountBeforeVat(row) {
  const num = Number(row?.totalPrice)
  return Number.isFinite(num) ? num : 0
}

const reportGrandTotal = computed(() =>
  reportRows.value.reduce((sum, row) => sum + lineAmountBeforeVat(row), 0)
)

function drawPdfHeaderFields(doc, startY, leftMargin, pageWidth, rightMargin) {
  const lineGap = 6
  let y = startY

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)

  doc.text('Purchaser Name________________________________________', leftMargin, y)
  y += lineGap
  doc.text('Purchaser TIN_________________________________________', leftMargin, y)
  y += lineGap

  const fromLabel = formatPdfDate(form.fromDate)
  const toLabel = form.uptoDate ? formatPdfDate(form.uptoDate) : '______________________'
  doc.text(`Report Date From ${fromLabel} to ${toLabel}`, leftMargin, y)
  y += lineGap

  doc.text('Phone Work:___________________________________________', leftMargin, y)
  y += lineGap
  doc.text('Mobile Number:________________________________________', leftMargin, y)
  y += lineGap
  doc.text('Business Sector:______________________________________', leftMargin, y)
  y += lineGap + 2

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(11)
  doc.text('Purchase Declaration', pageWidth / 2, y, { align: 'center' })
  y += 5

  doc.setDrawColor(0, 0, 0)
  doc.setLineWidth(0.2)
  doc.line(leftMargin, y, pageWidth - rightMargin, y)

  return y + 4
}

function drawPdfSignatureFooter(doc, startY, pageWidth, pageHeight, leftMargin, rightMargin) {
  let footerStartY = Math.max(startY + 10, pageHeight - 48)
  if (footerStartY + 28 > pageHeight - 8) {
    doc.addPage()
    footerStartY = pageHeight - 48
  }

  const rightX = pageWidth - rightMargin
  const stampBoxWidth = 36
  const stampBoxHeight = 22
  const lineGap = 8
  let y = footerStartY

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)

  doc.text('Prepared By________________________________', leftMargin, y)
  doc.setFont('helvetica', 'bold')
  doc.text('Company Stamp', rightX, y, { align: 'right' })
  doc.setDrawColor(0, 0, 0)
  doc.setLineWidth(0.2)
  doc.rect(rightX - stampBoxWidth, y + 2, stampBoxWidth, stampBoxHeight)

  y += lineGap
  doc.setFont('helvetica', 'normal')
  doc.text('Date_______________________________________', leftMargin, y)
  y += lineGap
  doc.text('Signature__________________________________', leftMargin, y)
}

async function downloadPurchaseDeclarationPdf() {
  const rows = reportRows.value
  if (!rows.length) {
    showToast('No purchase lines to download.', 'error')
    return
  }

  isDownloadingPdf.value = true
  try {
    const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' })
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()
    const leftMargin = 14
    const rightMargin = 14
    const topMargin = 10
    const totalPagesExp = '{total_pages_count_string}'
    const grandTotal = reportGrandTotal.value

    const tableBody = rows.map((row) => ([
      String(row.no ?? ''),
      row.sellerTin || '-',
      fmt(lineAmountBeforeVat(row)) || '-',
      row.receiptNumber || '-',
      row.receiptDate ? formatDisplayDate(row.receiptDate) : '-',
      row.purchaseType || 'Goods',
      row.mrcNumber || '-'
    ]))

    doc.setFont('helvetica', 'bold')
    doc.setFontSize(10)
    doc.text(String(printCompanyName.value || 'Company'), pageWidth / 2, topMargin + 4, { align: 'center' })
    const headerBottomY = drawPdfHeaderFields(doc, topMargin + 8, leftMargin, pageWidth, rightMargin)

    autoTable(doc, {
      head: [[
        'No.',
        'Seller TIN',
        'Amount Before Any VAT',
        'Receipt No.',
        'Receipt Date',
        'Purchase Type',
        'MRC'
      ]],
      body: tableBody,
      foot: [[
        { content: 'Grand Total', colSpan: 2, styles: { halign: 'right', fontStyle: 'bold' } },
        { content: fmt(grandTotal), styles: { halign: 'right', fontStyle: 'bold' } },
        { content: '', colSpan: 4 }
      ]],
      startY: headerBottomY,
      theme: 'grid',
      margin: { left: leftMargin, right: rightMargin, top: headerBottomY, bottom: 28 },
      styles: {
        fontSize: 8,
        cellPadding: 1.2,
        textColor: [0, 0, 0],
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
        overflow: 'linebreak'
      },
      headStyles: {
        fillColor: [243, 244, 246],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        halign: 'center'
      },
      footStyles: {
        fillColor: [241, 245, 249],
        textColor: [0, 0, 0],
        fontStyle: 'bold'
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 10 },
        2: { halign: 'right' },
        4: { halign: 'center' }
      },
      didDrawPage: (data) => {
        if (data.pageNumber > 1) {
          doc.setFont('helvetica', 'bold')
          doc.setFontSize(10)
          doc.text(String(printCompanyName.value || 'Company'), pageWidth / 2, topMargin + 4, { align: 'center' })
          doc.setFont('helvetica', 'bold')
          doc.setFontSize(11)
          doc.text('Purchase Declaration', pageWidth / 2, topMargin + 10, { align: 'center' })
        }
        doc.setFont('helvetica', 'normal')
        doc.setFontSize(8)
        doc.text(`Page ${doc.getNumberOfPages()} of ${totalPagesExp}`, pageWidth - rightMargin, pageHeight - 5, { align: 'right' })
      }
    })

    if (typeof doc.putTotalPages === 'function') {
      doc.putTotalPages(totalPagesExp)
    }

    const finalY = doc.lastAutoTable?.finalY || headerBottomY
    drawPdfSignatureFooter(doc, finalY, pageWidth, pageHeight, leftMargin, rightMargin)

    const safeFrom = String(form.fromDate || 'from').replace(/[\\/:*?"<>|]/g, '-').trim()
    const safeUpto = String(form.uptoDate || 'upto').replace(/[\\/:*?"<>|]/g, '-').trim()
    doc.save(`Purchase-Declaration-${safeFrom}-to-${safeUpto}.pdf`)
  } catch (error) {
    console.error('downloadPurchaseDeclarationPdf error:', error)
    showToast('Failed to generate PDF.', 'error')
  } finally {
    isDownloadingPdf.value = false
  }
}

async function generateReport() {
  const fromDate = String(form.fromDate || '').trim()
  if (!fromDate) {
    showToast('From date is required.', 'error')
    return
  }

  const uptoDate = String(form.uptoDate || '').trim()
  if (uptoDate && uptoDate < fromDate) {
    showToast('Upto date cannot be before From date.', 'error')
    return
  }

  const requestId = ++reportRequestId
  isLoadingReport.value = true
  hasGenerated.value = true

  try {
    const params = new URLSearchParams()
    params.set('fromDate', fromDate)
    if (uptoDate) params.set('uptoDate', uptoDate)

    const response = await apiFetch(`/finance/purchase-report?${params.toString()}`, {
      method: 'GET'
    })
    if (requestId !== reportRequestId) return

    if (!response.ok) {
      reportRows.value = []
      const data = await response.json().catch(() => ({}))
      showToast(data?.message || 'Failed to load purchase Declaration.', 'error')
      return
    }

    const rows = await response.json().catch(() => [])
    reportRows.value = Array.isArray(rows) ? rows : []
  } catch {
    if (requestId !== reportRequestId) return
    reportRows.value = []
    showToast('Failed to load purchase Declaration.', 'error')
  } finally {
    if (requestId === reportRequestId) {
      isLoadingReport.value = false
    }
  }
}
</script>

<style scoped>
.purchase-table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  color: #1e293b;
  border-collapse: separate;
  border-spacing: 0;
}

.purchase-table th,
.purchase-table td {
  padding: 4px 10px;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: middle;
  font-size: 0.72rem;
  line-height: 1.2;
  white-space: nowrap;
}

.purchase-table thead th {
  background: #f1f5f9;
  font-weight: 600;
  color: #1e293b;
  text-align: left;
  position: sticky;
  top: 0;
  z-index: 20;
}

.purchase-col-no {
  width: 2.75rem;
  min-width: 2.75rem;
  max-width: 2.75rem;
  text-align: center;
}

.purchase-table tbody td {
  text-align: left;
}

.purchase-table tbody tr:hover {
  background: #f8fafc;
}

.purchase-table th.num,
.purchase-table td.num {
  width: 5.5rem;
  min-width: 5.5rem;
  text-align: center;
}
</style>
